import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";

const statuses = [
  { value: "prospect", label: "Prospekt" },
  { value: "initial_contact", label: "Innledende Kontakt" },
  { value: "interested", label: "Noe Interessert" },
  { value: "info_sent", label: "Sendt Info og Følger Opp" },
  { value: "evaluation", label: "Evaluering" },
  { value: "letter_of_intent", label: "Intensjonsavtale" },
  { value: "negotiation_dd", label: "Forhandling/DD" },
  { value: "closed_won", label: "Gjennomført" },
];

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function AcquisitionCandidateForm({ candidate, onSubmit, onClose, isLoading, defaultCustomerId }) {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    customer_id: defaultCustomerId || "none",
    status: "prospect",
    discussed_price: "",
    ebitda: "",
    multiple: "",
    industry: "",
    employees: "",
    revenue: "",
    responsible_person: "",
    notes: "",
  });

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: customers = [], isLoading: customersLoading } = useQuery({
    queryKey: ["business-customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  useEffect(() => {
    if (candidate) {
      setFormData({
        company_name: candidate.company_name || "",
        contact_person: candidate.contact_person || "",
        contact_email: candidate.contact_email || "",
        contact_phone: candidate.contact_phone || "",
        customer_id: candidate.customer_id || "none",
        status: candidate.status || "prospect",
        discussed_price: candidate.discussed_price || "",
        ebitda: candidate.ebitda || "",
        multiple: candidate.multiple || "",
        industry: candidate.industry || "",
        employees: candidate.employees || "",
        revenue: candidate.revenue || "",
        responsible_person: candidate.responsible_person || "",
        notes: candidate.notes || "",
      });
    }
  }, [candidate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const responsibleUser = users.find(u => u.email === formData.responsible_person);
    const resolvedCustomerId = (!formData.customer_id || formData.customer_id === "none") ? null : formData.customer_id;
    const customer = customers.find(c => c.id === resolvedCustomerId);
    
    // Calculate multiple if both price and EBITDA are provided
    let calculatedMultiple = formData.multiple;
    if (formData.discussed_price && formData.ebitda && !formData.multiple) {
      calculatedMultiple = (parseFloat(formData.discussed_price) / parseFloat(formData.ebitda)).toFixed(2);
    }

    const data = {
      ...formData,
      customer_id: resolvedCustomerId,
      discussed_price: formData.discussed_price ? parseFloat(formData.discussed_price) : null,
      ebitda: formData.ebitda ? parseFloat(formData.ebitda) : null,
      multiple: calculatedMultiple ? parseFloat(calculatedMultiple) : null,
      employees: formData.employees ? parseInt(formData.employees) : null,
      revenue: formData.revenue ? parseFloat(formData.revenue) : null,
      responsible_person_name: responsibleUser ? getDisplayName(responsibleUser) : null,
      customer_name: customer?.company_name || null,
    };
    
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {candidate ? "Rediger Kandidat" : "Ny Oppkjøpskandidat"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        {(customersLoading || usersLoading) ? (
          <div className="p-12 flex items-center justify-center" data-testid="candidate-form-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="company_name">Bedriftsnavn *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_person">Kontaktperson *</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email">E-post</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_phone">Telefon</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsible_person">Ansvarlig Person</Label>
              <Select value={formData.responsible_person} onValueChange={(v) => setFormData({ ...formData, responsible_person: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Velg ansvarlig" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.email}>
                      {getDisplayName(user)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer_id">Relatert Kunde (valgfritt)</Label>
              <Select value={formData.customer_id || "none"} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                <SelectTrigger data-testid="candidate-customer-select">
                  <SelectValue placeholder="Velg kunde" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen kunde</SelectItem>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">Bransje</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-5">
            <h3 className="font-semibold text-slate-900 mb-4">Finansiell Informasjon</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <Label htmlFor="discussed_price">Pris Diskutert (NOK)</Label>
                <Input
                  id="discussed_price"
                  type="number"
                  value={formData.discussed_price}
                  onChange={(e) => setFormData({ ...formData, discussed_price: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ebitda">EBITDA (NOK)</Label>
                <Input
                  id="ebitda"
                  type="number"
                  value={formData.ebitda}
                  onChange={(e) => setFormData({ ...formData, ebitda: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="multiple">Multipel</Label>
                <Input
                  id="multiple"
                  type="number"
                  step="0.1"
                  value={formData.multiple}
                  onChange={(e) => setFormData({ ...formData, multiple: e.target.value })}
                  placeholder="Beregnes automatisk"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="revenue">Omsetning (NOK)</Label>
                <Input
                  id="revenue"
                  type="number"
                  value={formData.revenue}
                  onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employees">Antall Ansatte</Label>
                <Input
                  id="employees"
                  type="number"
                  value={formData.employees}
                  onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notater</Label>
            <Textarea
              id="notes"
              data-testid="candidate-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} data-testid="candidate-form-cancel">Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800" data-testid="candidate-form-submit">
              {isLoading ? "Lagrer..." : candidate ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}